import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, HeartHandshake } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LiveChatWidget from "@/components/LiveChatWidget";
import { Reveal } from "@/components/Reveal";

const PEOPLE = [
    {
        testid: "about-photo-owner",
        name: "Nicolas Joly Janelle",
        role: "Cofondateur — Relation propriétaires",
    },
    {
        testid: "about-photo-colleague",
        name: "Olivier Beauvais",
        role: "Cofondateur — Opérations & qualité",
    },
];

const titleLines = [
    <span key="t1">Deux passionnés,</span>,
    <span key="t2" className="italic text-[#8A7563]">une promesse simple.</span>,
];

export default function AboutPage() {
    return (
        <div className="min-h-screen bg-[#FAF8F5] text-[#2B2622] antialiased overflow-x-clip">
            <div className="noise-overlay" />
            <Navbar />
            <main className="pt-32">
                <section data-testid="about-page" className="mx-auto max-w-4xl px-6">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="eyebrow mb-8 flex items-center gap-3"
                    >
                        <span className="inline-block h-px w-10 bg-[#8A7563]" />
                        À propos de nous
                    </motion.p>
                    <h1 className="font-display text-4xl font-semibold leading-[1.08] sm:text-5xl lg:text-6xl">
                        {titleLines.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block"
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>
                </section>

                <section data-testid="about-story" className="mx-auto max-w-4xl px-6 py-16">
                    <Reveal>
                        <div className="space-y-6 text-base leading-relaxed text-[#5A4F44] md:text-lg">
                            <p>
                                Depuis plus de 5 ans, nous vivons l'univers Airbnb de l'intérieur,
                                partout au Québec. De Montréal à Charlevoix, en passant par
                                Tremblant et les Cantons-de-l'Est, nous avons séjourné dans des
                                dizaines de chalets, de condos et d'appartements — et nous avons vu
                                de près ce qui fait décoller une annonce… ou la couler.
                            </p>
                            <p>
                                Forts de cette expérience, nous avons créé Gestion Séjoura avec une
                                mission claire : aider les propriétaires de chalets, d'appartements
                                et de propriétés Airbnb à optimiser leur annonce et leurs revenus —
                                sans que la gestion devienne un deuxième emploi. Photos, ménage,
                                tarification, voyageurs : nous nous occupons de tout, et nous ne
                                nous payons que lorsque vous êtes payé.
                            </p>
                            <p className="border-l-2 border-[#8A7563]/40 pl-5 italic">
                                « Notre promesse : vous ne toucherez plus jamais à une éponge ni à
                                un calendrier. Vous encaissez, point. »
                                <span className="mt-2 block font-mono-accent text-[10px] uppercase not-italic tracking-[0.25em] text-[#8A7F72]">
                                    — Nicolas &amp; Olivier
                                </span>
                            </p>
                        </div>
                    </Reveal>
                </section>

                <section className="mx-auto max-w-4xl px-6 pb-20">
                    <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                        {PEOPLE.map((p, i) => (
                            <Reveal key={p.testid} delay={i * 0.12}>
                                <article
                                    data-testid={p.testid}
                                    className="rounded-2xl border border-[#8A7563]/20 bg-white p-8 shadow-[0_16px_50px_rgba(90,79,68,0.10)]"
                                >
                                    <span className="mb-5 inline-block h-px w-10 bg-[#8A7563]" />
                                    <h3 className="font-display text-3xl font-semibold">{p.name}</h3>
                                    <p className="mt-2 text-sm text-[#8A7F72]">{p.role}</p>
                                </article>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.2}>
                        <div className="mt-16 flex flex-col items-center gap-5 rounded-2xl border border-[#8A7563]/20 bg-gradient-to-b from-[#8A7563]/8 to-white p-10 text-center">
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#8A7563]/12 text-[#8A7563]">
                                <HeartHandshake size={22} strokeWidth={1.5} />
                            </span>
                            <h2 className="font-display text-3xl font-semibold">Envie de nous confier vos clés ?</h2>
                            <p className="max-w-md text-sm leading-relaxed text-[#8A7F72]">
                                L'étude de votre propriété est gratuite et sans engagement.
                                On se parle de vive voix, comme il se doit.
                            </p>
                            <div className="flex flex-wrap items-center justify-center gap-4">
                                <a
                                    data-testid="about-contact-button"
                                    href="/#contact"
                                    className="group flex items-center gap-3 rounded-full bg-[#8A7563] px-7 py-3.5 text-sm font-semibold text-[#FAF8F5] transition-all duration-300 hover:bg-[#6E5D4E] hover:shadow-[0_0_32px_rgba(138,117,99,0.35)]"
                                >
                                    Parler avec nous
                                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </a>
                                <a
                                    data-testid="about-back-home"
                                    href="/"
                                    className="flex items-center gap-2 rounded-full border border-[#2B2622]/15 px-7 py-3.5 text-sm font-medium text-[#2B2622] transition-all duration-300 hover:border-[#8A7563]/60 hover:text-[#8A7563]"
                                >
                                    <ArrowLeft size={16} /> Retour à l'accueil
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </section>
            </main>
            <Footer />
            <LiveChatWidget />
        </div>
    );
}
